import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { RadioOrSelectOption } from '../../../../../../types/common.types';
import { Member, Team, TeamMember } from '../../../../../../types/entity.types';

import Button from '../../../../../common/button/Button';
import Dropdown from '../../../../../common/input/dropdown/Dropdown';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Modal from '../../../../../common/modal/Modal';

import { ReactComponent as DeleteIcon } from '../../../../../../resources/icons/close.svg';

import '../team-modals.scss';

type Props = {
  show: boolean;
  close: () => void;
  members: Member[];
  createTeam: (teamToSave: Team, close: () => void) => void;
};

const AddTeamModal = ({ show, close, members, createTeam }: Props) => {
  const [name, setName] = useState<string>('');
  const [selectedMembers, setSelectedMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    if (!show) {
      setName('');
      setSelectedMembers([]);
    }
  }, [show]);

  const availableMembers = useMemo(() => {
    const filteredMembers = members.filter(
      (member) => !selectedMembers.some(({ externalReference }) => externalReference === member.externalReference)
    );

    const sortedMembers = filteredMembers.sort((memberOne, memberTwo) => {
      if (!memberOne.name || !memberTwo.name) return 0;
      if (memberOne.name > memberTwo.name) return 1;
      if (memberOne.name < memberTwo.name) return -1;
      return 0;
    });

    return sortedMembers.map((member) => ({
      label: `${member.firstName} ${member.lastName}`,
      value: member.externalReference!
    }));
  }, [members, selectedMembers]);

  if (!show) return <Fragment />;

  const handleDeleteMemberClick = (member: TeamMember) => {
    setSelectedMembers((prev) =>
      prev.filter(({ externalReference }) => externalReference !== member.externalReference)
    );
  };

  const handleSaveClick = () => {
    const teamToSave: Team = {
      teamName: name,
      memberExternalReferences: selectedMembers.map(({ externalReference }) => externalReference!)
    };

    createTeam(teamToSave, close);
  };

  const renderMemberList = () => {
    if (selectedMembers.length === 0) return <></>;

    return (
      <section className="add-team-modal__member-list visible-scrollbar">
        {selectedMembers.map((member) => (
          <div className="add-team-modal__selected-member" key={member.externalReference}>
            <p>{member.name}</p>
            <DeleteIcon onClick={() => handleDeleteMemberClick(member)} data-testid="remove-member-button" />
          </div>
        ))}
      </section>
    );
  };

  const handleDropdownChange = (option: RadioOrSelectOption) => {
    const selectedOption = option;
    const member: TeamMember = {
      name: selectedOption.label,
      externalReference: selectedOption.value
    };
    setSelectedMembers((prev) => [...prev, member]);
  };

  return (
    <Modal show={show}>
      <section className="team-modals">
        <h1 className="team-modals__header">Add Team</h1>
        <section className="add-team-modal__inputs">
          <TextInput
            label="Team Name:"
            name="name"
            value={name}
            onChange={({ target }) => setName(target.value)}
            autoFocus
          />

          <section className="add-team-modal__member-dropdown-container">
            <label className="add-team-modal__member-dropdown-label" htmlFor="members">
              Team Members:
            </label>
            <div className="add-team-modal__member-dropdown">
              {renderMemberList()}
              <Dropdown name="members" options={availableMembers} onChange={handleDropdownChange} />
            </div>
          </section>
        </section>
        <section className="team-modals__button-container">
          <Button text="Cancel" onClick={close} />
          <Button text="Save" onClick={handleSaveClick} disabled={!name} />
        </section>
      </section>
    </Modal>
  );
};

export default AddTeamModal;
